import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Card,
  Button,
  Dialog,
  Paper,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateSubjectChangeNameMutation,
  useGetallGradeQuery,
} from "../../../Api/attoDeskApi";
import { IGrade, ISubject , ISubjectEdit} from "../../../Api/Interface/api.interface";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import LoadingDialog from "../../../Components/LoadingDialog/LoadingDialog";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ISubject;
}

const ItemList = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "none",

  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,

  color: selected ? "#ffffff" : theme.palette.text.primary,

  "& .MuiTypography-root": {
    color: selected ? "#ffffff" : theme.palette.text.primary,
  },

  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
  },
}));

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  const [newCompany, { isLoading }] = useCreateSubjectChangeNameMutation();
  const isMobileRes = useIsMobile();

  const {
    data: gradeData,
    isLoading: gradeIsLoading,
    isError,
    refetch,
  } = useGetallGradeQuery({
    searchText: "",
  });

  const handleClose = () => {
    handleCloseDialog(false);
  };

  useEffect(() => {
    if (data?.grade_ids) {
      setSelectedCategories(data.grade_ids);
    }
  }, [data]);

  const formik = useFormik<ISubjectEdit>({
    initialValues: {
      id: Number(data?.id),
      subject_name: data?.subject_name || "",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          ...(data?.id ? { id: Number(values.id) } : {}),
          subject_name: toProperCase(values.subject_name),
        };

        const response = await newCompany(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.subject_name === "" ||
      formik.values.subject_name === undefined
      ? false
      : true;
  }, [formik]);

  if (gradeIsLoading)
    return (
      <LoadingDialog
        open={gradeIsLoading}
        title="Loading Data"
        message="Fetching grades, please wait..."
      />
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="md"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item pt={2} alignContent={"center"}>
                <Typography className={classes.cardTitle}>
                  {data ? "Edit Subject " : "Create Subject"}
                </Typography>
              </Grid>
              <Grid
                item
                pt={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Subject
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Subject"
                                size="small"
                                {...formik.getFieldProps("subject_name")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>{" "}
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          {isLoading && (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                              sx={{ marginRight: 1 }}
                            />
                          )}
                          {data ? "Update" : "Create"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
